/**
 * Chat Service
 *
 * This service handles all chat-related API calls to the Core API.
 * Provides AI-powered facility management assistance.
 *
 * @author FacilityAI Team
 * @version 1.0
 */

import { VITE_CORE_API_URL } from "@/constants/env";
import { auth } from "@/services/auth-service";

export interface ChatMessage {
	role: "user" | "assistant";
	content: string;
}

export interface ChatResponse {
	success: boolean;
	message: string;
	data?: {
		response: string;
		cards?: any[];
	};
}

export interface ChatResult {
	text: string;
	cards: any[];
}

export interface SuggestionsResponse {
	success: boolean;
	message: string;
	data?: {
		suggestions: string[];
	};
}

/**
 * Send a chat message and get AI response with optional rich cards
 *
 * @param messages - Array of chat messages (conversation history)
 * @returns Promise with the AI response text and card data
 */
export const sendChatMessage = async (
	messages: ChatMessage[]
): Promise<ChatResult> => {
	try {
		const response = await fetch(`${VITE_CORE_API_URL}/core/chat`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
			},
			body: JSON.stringify({ messages }),
		});

		if (!response.ok) {
			const errorData = await response.json().catch(() => ({}));
			throw new Error(errorData.message || "Failed to send message");
		}

		const result: ChatResponse = await response.json();
		
		if (!result.success || !result.data?.response) {
			throw new Error(result.message || "No response received");
		}

		return {
			text: result.data.response,
			cards: result.data.cards || [],
		};
	} catch (error) {
		console.error("Chat service error:", error);
		throw error;
	}
};

/**
 * Get suggested questions based on organization data
 *
 * @returns Promise with array of suggested questions
 */
export const getChatSuggestions = async (): Promise<string[]> => {
	try {
		const response = await fetch(`${VITE_CORE_API_URL}/core/chat/suggestions`, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
			},
		});

		if (!response.ok) {
			const errorData = await response.json().catch(() => ({}));
			throw new Error(errorData.message || "Failed to get suggestions");
		}

		const result: SuggestionsResponse = await response.json();
		
		if (!result.success || !result.data?.suggestions) {
			return getDefaultSuggestions();
		}

		return result.data.suggestions;
	} catch (error) {
		console.error("Failed to fetch suggestions:", error);
		return getDefaultSuggestions();
	}
};

/**
 * Default fallback suggestions if API fails
 */
const getDefaultSuggestions = (): string[] => [
	"Give me an overview of my facilities",
	"What assets need attention?",
	"How do I maintain HVAC equipment?",
	"Which locations have the most assets?",
];
